// Componentes de tabla del design-system propio (sobre @tanstack/react-table): DataTableToolbar
// (búsqueda + filtros extra vía children), DataTablePagination (paginación) y SortableHeader
// (encabezado de columna ordenable).
import { type ReactNode } from 'react';
import { type Column, type Table } from '@tanstack/react-table';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Input as UiInput } from '@/components/ui/input';
import { Button } from './forms';

export interface DataTableToolbarProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    children?: ReactNode;
    className?: string;
}

export function DataTableToolbar({
    value,
    onChange,
    placeholder = 'Buscar…',
    children,
    className,
}: DataTableToolbarProps) {
    return (
        <div className={cn('flex items-center gap-2 py-2', className)}>
            <div className="relative flex-1">
                <Search
                    className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                />
                <UiInput
                    value={value}
                    onChange={(event) => {
                        onChange(event.target.value);
                    }}
                    placeholder={placeholder}
                    className="pl-9"
                />
            </div>
            {children}
        </div>
    );
}

export interface DataTablePaginationProps<TData> {
    table: Table<TData>;
}

export function DataTablePagination<TData>({ table }: DataTablePaginationProps<TData>) {
    const { pageIndex } = table.getState().pagination;
    const pageCount = Math.max(table.getPageCount(), 1);
    return (
        <div className="flex items-center justify-between gap-2 py-2 text-sm">
            <span className="text-muted-foreground">
                Página {pageIndex + 1} de {pageCount}
            </span>
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!table.getCanPreviousPage()}
                    onClick={() => {
                        table.previousPage();
                    }}
                >
                    Anterior
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!table.getCanNextPage()}
                    onClick={() => {
                        table.nextPage();
                    }}
                >
                    Siguiente
                </Button>
            </div>
        </div>
    );
}

export interface SortableHeaderProps<TData, TValue> {
    column: Column<TData, TValue>;
    title: string;
    className?: string;
}

export function SortableHeader<TData, TValue>({
    column,
    title,
    className,
}: SortableHeaderProps<TData, TValue>) {
    const sorted = column.getIsSorted();
    return (
        <button
            type="button"
            onClick={() => {
                column.toggleSorting(sorted === 'asc');
            }}
            className={cn(
                'inline-flex items-center gap-1 text-left font-medium transition-colors hover:text-foreground',
                className,
            )}
        >
            {title}
            {sorted === 'asc' ? (
                <ChevronUp className="size-3.5" aria-hidden />
            ) : sorted === 'desc' ? (
                <ChevronDown className="size-3.5" aria-hidden />
            ) : (
                <ChevronDown className="size-3.5 opacity-30" aria-hidden />
            )}
        </button>
    );
}
